"use client";

import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../components/ui/card";
import { useAppAuth } from "@/hooks/useAppAuth";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { appConfig, saveRuntimeOverrides } from "@/appConfig";
import type { WelcomeLanguage } from "@/appConfig";
import api from "../http";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useColor } from "@/contexts/ColorContext";

const WELCOME_LANGUAGES: WelcomeLanguage[] = ["de", "en", "fr", "it"];

function restoreAssistantPlaceholder(message: string) {
  return message.split(appConfig.assistantName).join("{ASSISTANT_NAME}");
}

function buildWelcomeMessagesState(
  messages?: Partial<Record<WelcomeLanguage, string>>,
): Record<WelcomeLanguage, string> {
  return WELCOME_LANGUAGES.reduce(
    (acc, language) => ({
      ...acc,
      [language]: restoreAssistantPlaceholder(
        messages?.[language] || appConfig.welcomeMessages?.[language] || "",
      ),
    }),
    {} as Record<WelcomeLanguage, string>,
  );
}

export default function Settings() {
  const { getAccessToken, user } = useAppAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [assistantName, setAssistantName] = useState(appConfig.assistantName);
  const [activeWelcomeLanguage, setActiveWelcomeLanguage] =
    useState<WelcomeLanguage>("de");
  const [welcomeMessages, setWelcomeMessages] = useState<
    Record<WelcomeLanguage, string>
  >(() => buildWelcomeMessagesState(appConfig.welcomeMessages));
  const [assistantPhoto, setAssistantPhoto] = useState(
    appConfig.assistantPhoto || "",
  );
  const [isLoadingAppSettings, setIsLoadingAppSettings] = useState(true);
  const [isAssistantPhotoUploading, setIsAssistantPhotoUploading] = useState(false);
  const assistantPhotoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadCurrentUserAndSettings = async () => {
      try {
        const token = await getAccessToken();
        const [userData, appSettings] = await Promise.all([
          api.getCurrentUser(token),
          api.getAppSettings(token),
        ]);
        setCurrentUser(userData);
        setAssistantName(appSettings.assistant_name || appConfig.assistantName);
        setWelcomeMessages(
          buildWelcomeMessagesState(
            appSettings.welcome_messages || {
              ...appConfig.welcomeMessages,
              de: appSettings.welcome_message,
            },
          ),
        );
        setAssistantPhoto(appSettings.assistant_photo || "");
      } catch (error) {
        console.error("Failed to load current user:", error);
      } finally {
        setIsLoadingAppSettings(false);
      }
    };

    if (user) {
      loadCurrentUserAndSettings();
    }
  }, [user, getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  const handleAssistantPhotoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: t("settings.invalidType"),
        description: t("settings.invalidTypeDescription"),
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t("settings.fileTooLarge"),
        description: t("settings.fileTooLargeDescription"),
        variant: "destructive",
      });
      return;
    }

    setIsAssistantPhotoUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setAssistantPhoto(reader.result);
      }
      setIsAssistantPhotoUploading(false);
    };
    reader.onerror = () => {
      setIsAssistantPhotoUploading(false);
      toast({
        title: t("settings.uploadFailed"),
        description: t("settings.uploadFailedDescription"),
        variant: "destructive",
      });
    };
    reader.readAsDataURL(file);
  };

  const getAssistantInitials = () => {
    const name = assistantName.trim();
    if (!name) return "AI";
    return name.charAt(0).toUpperCase();
  };

  const handleSaveAppSettings = async () => {
    if (!assistantName.trim()) {
      toast({
        title: t("settings.nameMissing"),
        description: t("settings.nameMissingDescription"),
        variant: "destructive",
      });
      return;
    }
    const trimmedWelcomeMessages = WELCOME_LANGUAGES.reduce(
      (acc, language) => ({
        ...acc,
        [language]: welcomeMessages[language].trim(),
      }),
      {} as Record<WelcomeLanguage, string>,
    );

    const missingWelcomeLanguage = WELCOME_LANGUAGES.find(
      (language) => !trimmedWelcomeMessages[language],
    );

    if (missingWelcomeLanguage) {
      toast({
        title: t("settings.welcomeMissing"),
        description: `${t("settings.welcomeMissingDescription")} (${t(
          `language.${missingWelcomeLanguage}`,
        )})`,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSavingSettings(true);
      const token = await getAccessToken();
      const updated = await api.updateAppSettings(
        {
          assistant_name: assistantName.trim(),
          welcome_message: trimmedWelcomeMessages.de,
          welcome_messages: trimmedWelcomeMessages,
          assistant_photo: assistantPhoto.trim() || null,
        },
        token,
      );

      saveRuntimeOverrides({
        assistantName: updated.assistant_name,
        welcomeMessageTemplate: updated.welcome_message,
        welcomeMessages: updated.welcome_messages,
        assistantPhoto: updated.assistant_photo || "",
      });

      toast({
        title: t("settings.saved"),
        description: t("settings.savedDescription"),
      });
    } catch (error) {
      toast({
        title: t("settings.saveFailed"),
        description:
          error instanceof Error ? error.message : t("settings.unknownError"),
        variant: "destructive",
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-transparent admindiv">
      <header className="flex items-center justify-between px-8 py-4 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {t("settings.title")}
          </h1>
          <p className="text-sm text-slate-500">
            {t("settings.subtitle")}
          </p>
        </div>
        <SettingsDropdown
          onOpenProfileModal={() => setIsProfileModalOpen(true)}
          currentUser={currentUser}
        />
      </header>

      <div className="flex-1 overflow-auto">
        <div className="container mx-auto p-6 max-w-4xl">
          <Card className="border border-slate-200">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-medium text-slate-900">
                {t("settings.appConfig")}
              </h2>
              {isLoadingAppSettings ? (
                <div className="text-sm text-slate-500">
                  {t("settings.loading")}
                </div>
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="space-y-2">
                    <label className="text-slate-700 font-medium">
                      {t("settings.assistantName")}
                    </label>
                    <input
                      type="text"
                      value={assistantName}
                      onChange={(e) => setAssistantName(e.target.value)}
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      placeholder={t("settings.assistantNamePlaceholder")}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-slate-700 font-medium">
                      Welcome messages
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {WELCOME_LANGUAGES.map((language) => (
                        <button
                          key={language}
                          type="button"
                          onClick={() => setActiveWelcomeLanguage(language)}
                          className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                            activeWelcomeLanguage === language
                              ? "border-slate-400 bg-slate-100 text-slate-900"
                              : "border-slate-200 bg-white text-gray-700 hover:bg-slate-50"
                          }`}
                        >
                          {t(`language.${language}`)}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={welcomeMessages[activeWelcomeLanguage]}
                      onChange={(e) =>
                        setWelcomeMessages((current) => ({
                          ...current,
                          [activeWelcomeLanguage]: e.target.value,
                        }))
                      }
                      rows={7}
                      className="w-full rounded-lg border border-slate-200 bg-white p-2 text-slate-900"
                      placeholder={t("settings.welcomePlaceholder")}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-slate-700 font-medium">
                      {t("settings.assistantPhoto")}
                    </label>
                    <input
                      ref={assistantPhotoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAssistantPhotoFile}
                      className="hidden"
                    />
                    <div className="flex items-center gap-4">
                      <div
                        className="relative group cursor-pointer"
                        onClick={() => assistantPhotoInputRef.current?.click()}
                      >
                        <Avatar className="w-20 h-20 group-hover:ring-2 group-hover:ring-blue-400 transition-all">
                          <AvatarImage
                            src={assistantPhoto || undefined}
                            alt="Assistant photo"
                          />
                          <AvatarFallback className="text-lg font-medium">
                            {getAssistantInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                        {isAssistantPhotoUploading && (
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500">
                          {t("settings.clickPhoto")}
                        </p>
                        {assistantPhoto && (
                          <button
                            type="button"
                            onClick={() => setAssistantPhoto("")}
                            className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-slate-50"
                          >
                            {t("settings.removePhoto")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSaveAppSettings}
                      disabled={isSavingSettings}
                      className="inline-flex h-10 items-center justify-center rounded-[1234px] border px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                      style={{
                        backgroundColor: APP_COLOR,
                        borderColor: APP_COLOR,
                      }}
                    >
                      {isSavingSettings ? t("common.saving") : t("common.save")}
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {isProfileModalOpen && (
        <ProfileImageModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          onProfileImageUpdate={handleProfileImageUpdate}
          onUserInfoUpdate={handleUserInfoUpdate}
          currentUser={currentUser}
        />
      )}
    </div>
  );
}
